import React, { useMemo, useState } from 'react'
import { Tab, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tabs } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { newThemeObject } from '../../newTheme'
import { Position } from '../../redux/reducers/portfolio'
import { Security } from '../../redux/reducers/securities'
import { useSegmentedTabsStyles } from '../../styles/segmentedTabs'
import { tokens } from '../../styles/tokens'
import { computeBondSensitivity, priceChange, SCENARIOS_BP } from '../../utils/bondSensitivity'
import { formatNumber } from '../../utils/numbersFormatter'

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  title: {
    margin: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
  },
  scroll: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 1000,
    '& .MuiTableCell-root': {
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      padding: '16px 8px',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      ...newThemeObject.table.tableHeadChildren,
      padding: '0 8px 8px',
    },
    '& .MuiTableCell-root:first-child': {
      paddingLeft: newThemeObject.table.tableContentFirst.paddingLeft,
      position: 'sticky',
      left: 0,
      zIndex: 1,
      backgroundColor: tokens.color.white,
      [theme.breakpoints.down('sm')]: {
        whiteSpace: 'normal',
        minWidth: 120,
        maxWidth: 120,
        paddingLeft: 0,
      },
    },
    '& .MuiTableCell-root:last-child': {
      paddingRight: newThemeObject.table.tableContentFirst.paddingLeft,
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
    },
    '& .MuiTableBody-root .MuiTableRow-root:first-child .MuiTableCell-root': {
      borderTop: 'none',
    },
  },
  row: {
    transition: `background-color ${tokens.transition.fast}`,
    '&:hover, &:hover .MuiTableCell-root:first-child': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  headText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  text: {
    margin: 0,
    fontSize: '0.7rem',
    fontWeight: 400,
    color: tokens.color.neutral[900],
  },
  name: {
    margin: 0,
    fontSize: '0.7rem',
    fontWeight: 500,
    color: tokens.color.neutral[900],
  },
  isin: {
    margin: 0,
    fontSize: '0.6875rem',
    color: tokens.color.neutral[500],
  },
  muted: {
    color: tokens.color.neutral[400],
  },
  change: {
    margin: 0,
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  // Stejne barvy jako P&L v tabulce pozic.
  positive: {
    color: tokens.color.success[700],
  },
  negative: {
    color: tokens.color.primary[500],
  },
  totalRow: {
    '& .MuiTableCell-root, & .MuiTableCell-root:first-child': {
      backgroundColor: tokens.color.primary[50],
      borderTop: `1px solid ${tokens.color.neutral[200]}`,
      borderBottom: 'none',
    },
  },
  totalLabel: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
  },
  note: {
    margin: 0,
    fontSize: '0.7rem',
    color: tokens.color.neutral[500],
  },
}))

type Mode = 'amount' | 'security' | 'percent'

interface BondSensitivityTableProps {
  positions: Position[]
  securities: Security[]
  currency: string
}

/** Papir muze byt v DB vicekrat se stejnym ISIN a kupon mit vyplneny jen v jednom radku. */
const findBondParams = (position: Position, securities: Security[]): Security | null => {
  const isComplete = (security: Security) => !!security.coupon && !!security.maturityDate
  const own = securities.find(security => security.securityId === position.instrument.instrumentId)
  if (own && isComplete(own)) return own
  const { isin } = position.instrument
  return (isin && securities.find(security => security.isin === isin && isComplete(security))) || null
}

const BondSensitivityTable: React.FC<BondSensitivityTableProps> = ({ positions, securities, currency }) => {
  const classes = useStyles()
  const segTabs = useSegmentedTabsStyles()
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('percent')

  const rows = useMemo(
    () =>
      positions.map(position => {
        const params = findBondParams(position, securities)
        const sensitivity = params ? computeBondSensitivity(params, position.lastPrice) : null
        // Hodnota v mene portfolia vcetne AUV, kurz je obsazeny v pomeru objemu.
        const dirtyRatio = sensitivity ? sensitivity.dirtyPrice / position.lastPrice : 0
        return {
          position,
          sensitivity,
          baseValue: position.portfolioCurrencyAmount * dirtyRatio,
          securityBaseValue: position.amount * dirtyRatio,
        }
      }),
    [positions, securities]
  )

  type Row = typeof rows[number]
  const rowBase = (row: Row) => (mode === 'security' ? row.securityBaseValue : row.baseValue)
  const missingCount = rows.filter(row => !row.sensitivity).length

  const formatChange = (ratio: number, baseValue: number, securityCurrency: string) => {
    if (mode === 'percent') {
      const value = ratio * 100
      return `${value > 0 ? '+' : ''}${formatNumber(value)} %`
    }
    const value = ratio * baseValue
    const amount = `${value > 0 ? '+' : ''}${formatNumber(value, { decimalPlaces: 0 })}`
    return mode === 'security' ? `${amount} ${securityCurrency}` : amount
  }

  // Ruzne meny CP nejdou secist, v rezimu meny CP je proto soucet za kazdou menu zvlast.
  const calculated = rows.filter(row => row.sensitivity)
  const totalCurrencies =
    mode === 'security' ? Array.from(new Set(calculated.map(row => row.position.instrument.currency))) : [currency]
  const totals = calculated.length
    ? totalCurrencies.map(code => {
        const totalRows = mode === 'security' ? calculated.filter(row => row.position.instrument.currency === code) : calculated
        return { code, rows: totalRows, base: totalRows.reduce((sum, row) => sum + rowBase(row), 0) }
      })
    : []

  const totalRatio = (totalRows: Row[], base: number, bp: number) =>
    base > 0 ? totalRows.reduce((sum, row) => sum + priceChange(row.sensitivity!, bp) * rowBase(row), 0) / base : 0

  return (
    <>
      <div className={classes.header}>
        <p className={classes.title}>{t('BOND_SENSITIVITY_TABLE_TITLE')}</p>
        <Tabs className={segTabs.tabs} value={mode} onChange={(_, value: Mode) => setMode(value)}>
          <Tab
            className={segTabs.tab}
            value="amount"
            label={`${t('BOND_SENSITIVITY_MODE_AMOUNT')} ${currency}`}
          />
          <Tab className={segTabs.tab} value="security" label={t('BOND_SENSITIVITY_MODE_SECURITY')} />
          <Tab className={segTabs.tab} value="percent" label={t('BOND_SENSITIVITY_MODE_PERCENT')} />
        </Tabs>
      </div>
      <div className={classes.scroll}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <p className={classes.headText}>{t('BOND_SENSITIVITY_BOND')}</p>
              </TableCell>
              <TableCell align="right">
                <p className={classes.headText}>{t('BOND_SENSITIVITY_DURATION')}</p>
              </TableCell>
              {SCENARIOS_BP.map(bp => (
                <TableCell key={bp} align="right">
                  <p className={classes.headText}>{bp > 0 ? `+${bp}` : bp} bp</p>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const { position, sensitivity } = row
              return (
              <TableRow key={position.positionId} className={classes.row}>
                <TableCell>
                  <p className={classNames(classes.name, !sensitivity && classes.muted)}>{position.instrument.name}</p>
                  <p className={classes.isin}>{position.instrument.isin}</p>
                </TableCell>
                <TableCell align="right">
                  <p className={classNames(classes.text, !sensitivity && classes.muted)}>
                    {sensitivity ? formatNumber(sensitivity.modifiedDuration) : '–'}
                  </p>
                </TableCell>
                {SCENARIOS_BP.map(bp => {
                  if (!sensitivity) {
                    return (
                      <TableCell key={bp} align="right">
                        <p className={classNames(classes.text, classes.muted)}>–</p>
                      </TableCell>
                    )
                  }
                  const ratio = priceChange(sensitivity, bp)
                  return (
                    <TableCell key={bp} align="right">
                      <p className={classNames(classes.change, ratio > 0 ? classes.positive : classes.negative)}>
                        {formatChange(ratio, rowBase(row), position.instrument.currency)}
                      </p>
                    </TableCell>
                  )
                })}
              </TableRow>
              )
            })}
          </TableBody>
          {totals.length > 0 && (
            <TableFooter>
              {totals.map(total => (
                <TableRow key={total.code} className={classes.totalRow}>
                  <TableCell>
                    <p className={classes.totalLabel}>
                      {t('BOND_SENSITIVITY_TOTAL')} {total.code}
                    </p>
                  </TableCell>
                  <TableCell />
                  {SCENARIOS_BP.map(bp => {
                    const ratio = totalRatio(total.rows, total.base, bp)
                    return (
                      <TableCell key={bp} align="right">
                        <p className={classNames(classes.change, ratio > 0 ? classes.positive : classes.negative)}>
                          {formatChange(ratio, total.base, total.code)}
                        </p>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
      </div>
      <p className={classes.note}>
        {t('BOND_SENSITIVITY_NOTE')}
        {missingCount > 0 && ` ${t('BOND_SENSITIVITY_MISSING', { count: missingCount })}`}
      </p>
    </>
  )
}

export default BondSensitivityTable
